import { ConfigFetchError } from '@/lib/customErrors';
import * as Sentry from '@sentry/browser';
import { Effect, Schedule, Data, Exit, Cause, Option, Match, pipe } from 'effect';

// The unauthorized code is project-wide configuration, not a local constant — the
// same `REACT_APP_UNAUTHORIZED_CODE` is used by `@/domain/api.js`,
// `@/services/api/interceptors.ts`, `@/domain/documents/sagas` and `@/lib/worker/contextWorker`.
// Read per call rather than at module load so it is stubbable in tests, and fall back
// to 401 so an unset variable cannot silently make unauthorized responses retryable.
const unauthorizedStatus = () => Number(process.env.REACT_APP_UNAUTHORIZED_CODE) || 401;

export const RETRY_TIMES = 2;

export const RETRY_BASE_DELAY = '500 millis';

export const COGNITO_CONFIG_PATH = '/v2/cognito/get_configuration';

export const ENV_CONFIG_PATH = '/v2/frontend/get_configuration';

export class ConfigResponseError extends Data.TaggedError('ConfigResponseError')<{
  readonly status: number;
}> {}

export class ConfigNetworkError extends Data.TaggedError('ConfigNetworkError')<{
  readonly originalError: unknown;
}> {}

export type ConfigRequestError = ConfigResponseError | ConfigNetworkError;

// Every failure is worth another attempt except 401: the credentials the request
// carries were just obtained and the server rejected them, so an identical retry
// would be rejected too. Exhaustive, so adding a third failure type is a compile
// error rather than a silent fall-through to "retryable".
export const isRetryable = Match.type<ConfigRequestError>().pipe(
  Match.tag('ConfigNetworkError', () => true),
  Match.tag('ConfigResponseError', ({ status }) => status !== unauthorizedStatus()),
  Match.exhaustive,
);

const describeError = Match.type<ConfigRequestError>().pipe(
  Match.tag('ConfigResponseError', ({ status }) => `Responded with HTTP ${status}`),
  Match.tag(
    'ConfigNetworkError',
    ({ originalError }) => `Request failed before a response was received: ${String(originalError)}`,
  ),
  Match.exhaustive,
);

// Falls back to the pretty-printed cause so a defect is described too, rather
// than being reported as an empty failure.
export const describeCause = (cause: Cause.Cause<ConfigRequestError>) =>
  pipe(
    Cause.failureOption(cause),
    Option.map(describeError),
    Option.getOrElse(() => Cause.pretty(cause)),
  );

// Shared by both startup configuration requests, so there is one retry policy
// rather than a copy per call site.
const fetchConfig = (path: string, headers: Record<string, string>) =>
  Effect.tryPromise({
    try: () =>
      fetch(`${process.env.REACT_APP_ENV_CONFIG_API_URL}${path}`, {
        method: 'GET',
        headers,
      }),
    // A network-level rejection (offline, DNS) previously escaped both initializers
    // uncaught; it is now a typed, retryable failure.
    catch: (originalError) => new ConfigNetworkError({ originalError }),
  }).pipe(
    // The status check is what makes retry meaningful: without it an error page
    // returned instead of JSON fails later as a parse error, which no retry can fix.
    Effect.filterOrFail(
      (response) => response.ok,
      (response) => new ConfigResponseError({ status: response.status }),
    ),
    // 2 retries (3 attempts total), delaying 500ms then 1s. `times` bounds the
    // attempt count, `schedule` the delay, `while` excludes 401.
    Effect.retry({
      times: RETRY_TIMES,
      schedule: Schedule.exponential(RETRY_BASE_DELAY),
      while: isRetryable,
    }),
  );

// runPromiseExit rather than runPromise: the outcome arrives as a value, so the
// failure Cause stays inspectable (covering defects as well as typed failures)
// instead of being buried in the FiberFailure wrapper a rejection would carry.
const runConfigRequest = (path: string, headers: Record<string, string> = {}) =>
  Effect.runPromiseExit(fetchConfig(path, headers));

// Effect captures failures internally instead of letting them propagate, so
// Sentry must be called explicitly. Called at the single boundary below once the
// retry policy is exhausted — not once per attempt.
const reportEnvConfigFailure = (cause: Cause.Cause<ConfigRequestError>) => {
  console.error('Failed to get envs');
  Sentry.captureMessage('Server config fetch failed', {
    level: 'warning',
    tags: { source: 'SERVER_CONFIG_FETCH_FAILED' },
    extra: {
      cause: describeCause(cause),
    },
  });
};

export const requestEnvConfig = async (jwtToken: string) =>
  Exit.match(await runConfigRequest(ENV_CONFIG_PATH, { Authorization: `Bearer ${jwtToken}` }), {
    onFailure: (cause) => {
      reportEnvConfigFailure(cause);

      // throwing error to catch it in the main flow and render SWW page in case error is instance of ConfigFetchError
      throw new ConfigFetchError('Failed to fetch env config file');
    },
    onSuccess: (response) => response,
  });

export const requestCognitoConfig = async () =>
  Exit.match(await runConfigRequest(COGNITO_CONFIG_PATH), {
    onFailure: (cause) => {
      // Reporting is deliberately left to awsAmplifyInitializer's existing catch: it
      // already sits outside the retry and reports the SERVER_CONFIG tag exactly once,
      // so reporting here too would double-report every exhausted retry.
      throw new Error(`Failed to fetch cognito config. ${describeCause(cause)}`);
    },
    onSuccess: (response) => response,
  });
